import type { ChatAttachment, ChatStory, ResourceType } from "../types";

export interface GroupedLogs {
  attachment?: ChatAttachment;
  batchId?: string;
  commentBatchId?: string;
  createdAt?: Date;
  id: string;
  isEdited?: boolean;
  items: Array<{
    asanaResourceSubtype?: null | ResourceType;
    asanaResourceType?: null | ResourceType;
    attachment?: ChatAttachment;
    attachmentId?: number;
    id: string;
    isResolved?: boolean | null;
    message: string;
    name?: string;
    newValue?: string;
    oldValue?: string;
    timestamp: Date;
    url?: string;
    user?: {
      id: null | number;
      name: null | string;
      profilePhotoPath: null | string;
      utcOffset: null | string;
    };
  }>;
  type: "grouped_attachments" | "grouped_feedback" | "grouped_logs";
  user?: {
    id: null | number;
    name: null | string;
    profilePhotoPath: null | string;
    utcOffset: null | string;
  };
}

function checkImageOrVideoExtension(url: string): boolean {
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
  const urlLower = url.toLowerCase();

  return (
    imageExtensions.some((ext) => urlLower.endsWith(ext)) ||
    videoExtensions.some((ext) => urlLower.endsWith(ext))
  );
}

export const activityChatTypes = new Set([
  String.raw`App\Models\Request-activity-assignee`,
  String.raw`App\Models\Request-activity-brand`,
  String.raw`App\Models\Request-activity-created`,
  String.raw`App\Models\Request-activity-design type`,
  String.raw`App\Models\Request-activity-due_on`,
  String.raw`App\Models\Request-activity-name`,
  String.raw`App\Models\Request-activity-status`,
  String.raw`App\Models\Request-activity-brand`,
  String.raw`App\Models\Request-activity-due_on`,
  String.raw`App\Models\Request-activity-sizes_needed`,
  String.raw`App\Models\Request-activity-creative_direction`,
]);

function checkValidActitivyType(chatType: string) {
  return (
    chatType.includes(`App\\Models\\Request-activity`) &&
    chatType !== `App\\Models\\Request-activity-markup_comment_added`
  );
}

export function groupConsecutiveActivityLogs(
  messages: ChatStory[],
): (ChatStory | GroupedLogs)[] {
  const result: (ChatStory | GroupedLogs)[] = [];
  let i = 0;

  while (i < messages.length) {
    const currentMessage = messages[i];

    if (!currentMessage) {
      i++;
      continue;
    }

    if (checkValidActitivyType(currentMessage.chatType)) {
      const consecutiveLogs: ChatStory[] = [currentMessage];
      let j = i + 1;

      while (j < messages.length) {
        const nextMessage = messages[j];

        if (!nextMessage || !checkValidActitivyType(nextMessage.chatType)) {
          break;
        }
        consecutiveLogs.push(nextMessage);
        j++;
      }

      if (consecutiveLogs.length > 3) {
        const lastLog = consecutiveLogs.at(-1);

        if (!lastLog) {
          result.push(...consecutiveLogs);
          i = j;
          continue;
        }

        const groupedLogs: GroupedLogs = {
          createdAt: new Date(consecutiveLogs[0]?.createdAt || ""),
          id: `grouped_${consecutiveLogs[0]?.id}_${lastLog.id}`,
          items: consecutiveLogs.map((log) => ({
            asanaResourceSubtype: log.asanaResourceSubtype,
            id: log.id.toString(),
            message: log.comment || "",
            newValue: log.newValue || "",
            oldValue: log.oldValue || "",
            timestamp: new Date(log.createdAt || ""),
          })),
          type: "grouped_logs",
        };

        result.push(groupedLogs);
      } else {
        result.push(...consecutiveLogs);
      }

      i = j;
    } else {
      result.push(currentMessage);
      i++;
    }
  }

  return result;
}

export function groupMarkupCommentsWithAttachments(
  messages: ChatStory[],
): (ChatStory | GroupedLogs)[] {
  const result: (ChatStory | GroupedLogs)[] = [];
  const processedIndices = new Set<number>();

  for (const [i, currentMessage] of messages.entries()) {
    if (processedIndices.has(i) || !currentMessage) {
      continue;
    }

    if (
      currentMessage.chatType ===
      `App\\Models\\Request-activity-markup_comment_added`
    ) {
      const commentBatchId = currentMessage.attachmentId;

      if (!commentBatchId) {
        result.push(currentMessage);
        processedIndices.add(i);
        continue;
      }

      const subordinateAttachments: ChatStory[] = [];

      for (const [j, message] of messages.entries()) {
        if (j <= i || processedIndices.has(j) || !message) {
          continue;
        }

        if (
          message.chatType === `App\\Models\\Attachment-story-comment_added` &&
          message.parentId === commentBatchId
        ) {
          subordinateAttachments.push(message);
          processedIndices.add(j);
        }
      }

      if (subordinateAttachments.length > 0) {
        const groupedLogs: GroupedLogs = {
          attachment: currentMessage.attachment,
          createdAt: new Date(currentMessage.createdAt || ""),
          id: `markup_group_${currentMessage.id}_${commentBatchId}`,
          items: [
            {
              id: currentMessage.id.toString(),
              message: currentMessage.comment || "",
              timestamp: new Date(currentMessage.createdAt || ""),
            },
            ...subordinateAttachments.map((attachment) => ({
              id: attachment.id.toString(),
              isResolved: attachment.isResolved,
              message: attachment.comment || "",
              timestamp: new Date(attachment.createdAt || ""),
            })),
          ],
          type: "grouped_feedback",
          user: currentMessage.user,
        };

        result.push(groupedLogs);
      } else {
        result.push(currentMessage);
      }

      processedIndices.add(i);
    } else {
      result.push(currentMessage);
      processedIndices.add(i);
    }
  }

  // Now save the grouped feedbacks on an array and if the same attachmentId is found, replace the comment with the grouped feedback
  const groupedFeedbacks: GroupedLogs[] = [];

  for (const message of result) {
    if (isGroupedFeedback(message)) {
      groupedFeedbacks.push(message);
    }
  }

  // Create a map of attachmentId to grouped feedback for O(1) lookup
  const feedbackByAttachmentId = new Map<number, GroupedLogs>();

  for (const feedback of groupedFeedbacks) {
    // Extract attachmentId from the grouped feedback ID or attachment
    const attachmentId = feedback.attachment?.id;

    if (attachmentId) {
      feedbackByAttachmentId.set(attachmentId, feedback);
    }
  }

  // Replace markup comments with corresponding grouped feedbacks
  return result.map((message) => {
    if (
      isChatStory(message) &&
      message.chatType ===
        `App\\Models\\Request-activity-markup_comment_added` &&
      message.attachmentId
    ) {
      const correspondingFeedback = feedbackByAttachmentId.get(
        message.attachmentId,
      );

      if (correspondingFeedback) {
        correspondingFeedback.items[0] = {
          id: message.id.toString(),
          message: message.comment || "",
          timestamp: new Date(message.createdAt || ""),
        };
        correspondingFeedback.createdAt = new Date(message.createdAt || "");
      }

      if (correspondingFeedback) {
        return correspondingFeedback;
      }
    }

    return message;
  });
}

export function groupAttachmentsByCommentBatchId(
  messages: ChatStory[],
): (ChatStory | GroupedLogs)[] {
  const result: (ChatStory | GroupedLogs)[] = [];
  const processedIndices = new Set<number>();
  const batchGroups = new Map<
    string,
    { attachments: ChatStory[]; comment?: ChatStory; firstIndex: number }
  >();

  for (const [i, message] of messages.entries()) {
    if (!message) continue;

    if (message.commentBatchId && message.asanaResourceType !== "activity") {
      if (!batchGroups.has(message.commentBatchId)) {
        batchGroups.set(message.commentBatchId, {
          attachments: [],
          firstIndex: i,
        });
      }
      batchGroups.get(message.commentBatchId)!.attachments.push(message);
      processedIndices.add(i);
    }
  }

  for (const [i, message] of messages.entries()) {
    if (processedIndices.has(i)) {
      for (const [batchId, group] of batchGroups.entries()) {
        if (group.firstIndex === i && group.attachments.length > 1) {
          const items = [];

          if (group.comment) {
            items.push({
              id: group.comment.id.toString(),
              isEdited: group.comment.isEdited,
              message: group.comment.comment || "",
              timestamp: new Date(group.comment.createdAt || ""),
            });
          }

          items.push(
            ...group.attachments.map((attachment) => ({
              asanaResourceType: attachment.asanaResourceType,
              attachment: attachment.attachment,
              attachmentId: attachment.attachment?.id,
              id: attachment.id.toString(),
              isEdited: attachment.isEdited,
              message: attachment.comment || "",
              name: attachment.attachment?.name || "",
              timestamp: new Date(attachment.createdAt || ""),
              url: attachment.attachment?.url || "",
              user: attachment.user,
            })),
          );

          const groupedAttachments: GroupedLogs = {
            commentBatchId: group.attachments[0]?.commentBatchId || undefined,
            createdAt: new Date(group.attachments[0]?.createdAt || ""),
            id: `attachment_group_${batchId}`,
            isEdited: items.some((item) => "isEdited" in item && item.isEdited),
            items,
            type: "grouped_attachments",
            user: group.attachments[0]?.user,
          };

          result.push(groupedAttachments);
          break;
        } else if (group.firstIndex === i && group.attachments.length === 1) {
          if (group.comment) {
            result.push(group.comment);
          }
          const singleAttachment = group.attachments[0];

          if (singleAttachment) {
            result.push(singleAttachment);
          }
          break;
        }
      }
    } else if (message) {
      result.push(message);
    }
  }

  return result;
}

export function removeUnwantedMessages(messages: ChatStory[]): ChatStory[] {
  return messages.filter(
    (message) =>
      message.chatType !==
      `App\\Models\\Attachment-attachment-attachment_added`,
  );
}

export function parseAllChatMessages(
  messages: ChatStory[],
): (ChatStory | GroupedLogs)[] {
  let result = groupConsecutiveActivityLogs(messages);

  result = removeUnwantedMessages(result as ChatStory[]);

  result = groupMarkupCommentsWithAttachments(result as ChatStory[]);

  result = groupAttachmentsByCommentBatchId(result as ChatStory[]);

  return result;
}

export function isGroupedLogs(
  item: ChatStory | GroupedLogs,
): item is GroupedLogs {
  return "type" in item;
}

export function isChatStory(item: ChatStory | GroupedLogs): item is ChatStory {
  return "chatType" in item;
}

export function isActivityLog(
  item: ChatStory | GroupedLogs,
): item is ChatStory {
  return isChatStory(item) && checkValidActitivyType(item.chatType);
}

export function isGroupedAttachments(
  item: ChatStory | GroupedLogs,
): item is GroupedLogs {
  return isGroupedLogs(item) && item.type === "grouped_attachments";
}

export function isGroupedFeedback(
  item: ChatStory | GroupedLogs,
): item is GroupedLogs {
  return isGroupedLogs(item) && item.type === "grouped_feedback";
}

export function isGroupedActivityLogs(
  item: ChatStory | GroupedLogs,
): item is GroupedLogs {
  return isGroupedLogs(item) && item.type === "grouped_logs";
}

export function isImageAttachmentOrVideoAttachment(
  item: ChatStory | GroupedLogs,
): item is ChatStory {
  return (
    isChatStory(item) &&
    (item.chatType === `App\\Models\\Request-attachment-attachment_added` ||
      item.chatType === `App\\Models\\Request-attachment_added-attachment`) &&
    checkImageOrVideoExtension(item.comment || "")
  );
}

export function isRandomFileAttachment(
  item: ChatStory | GroupedLogs,
): item is ChatStory {
  return (
    isChatStory(item) &&
    (item.chatType === `App\\Models\\Request-attachment-attachment_added` ||
      item.chatType === `App\\Models\\Request-attachment_added-attachment`)
  );
}

// Status action types for real-time updates
export interface StatusAction {
  data: {
    batchId?: string;
    comment?: string;
    id?: number | string;
  };
  type:
    | "all_attachments_deleted"
    | "attachment_deleted"
    | "message_deleted"
    | "message_edited";
}

/**
 * Applies status actions to the chat messages array
 * Handles real-time updates from server-sent events
 */
export function applyStatusAction(
  messages: (ChatStory | GroupedLogs)[],
  action: StatusAction,
): (ChatStory | GroupedLogs)[] {
  switch (action.type) {
    case "message_edited": {
      return handleMessageEdited(messages, action.data);
    }

    case "message_deleted": {
      return handleMessageDeleted(messages, action.data);
    }

    case "all_attachments_deleted": {
      return handleAllAttachmentsDeleted(messages, action.data);
    }

    case "attachment_deleted": {
      return handleAttachmentDeleted(messages, action.data);
    }

    default: {
      console.warn("Unknown status action type:", action);

      return messages;
    }
  }
}

/**
 * Handles message editing by updating the comment content
 */
function handleMessageEdited(
  messages: (ChatStory | GroupedLogs)[],
  data: { comment?: string; id?: number | string },
): (ChatStory | GroupedLogs)[] {
  const messageId = Number(data.id);
  const newComment = data.comment || "";

  return messages.map((item) => {
    if (isChatStory(item) && item.id === messageId) {
      return {
        ...item,
        comment: newComment,
        isEdited: true,
        updatedAt: new Date().toISOString(),
      };
    }

    // Handle grouped messages
    if (isGroupedLogs(item)) {
      const updatedItems = item.items.map((subItem) => {
        if (Number(subItem.id) === messageId) {
          return {
            ...subItem,
            isEdited: true,
            message: newComment,
            timestamp: new Date(),
          };
        }

        return subItem;
      });

      // Only return updated group if something changed
      if (
        updatedItems.some(
          (updatedItem, index) => updatedItem !== item.items[index],
        )
      ) {
        return {
          ...item,
          items: updatedItems,
        };
      }
    }

    return item;
  });
}

/**
 * Handles message deletion by removing the message or entire groups
 */
function handleMessageDeleted(
  messages: (ChatStory | GroupedLogs)[],
  data: { id?: number | string },
): (ChatStory | GroupedLogs)[] {
  const messageId = Number(data.id);

  return messages.filter((item) => {
    // Remove individual chat stories
    if (isChatStory(item) && item.id === messageId) {
      return false;
    }

    // Handle grouped messages
    if (isGroupedLogs(item)) {
      const filteredItems = item.items.filter(
        (subItem) => Number(subItem.id) !== messageId,
      );

      // If all items were removed, remove the entire group
      if (filteredItems.length === 0) {
        return false;
      }

      // If only one item remains and it's not feedback, ungroup it
      if (filteredItems.length === 1 && item.type !== "grouped_feedback") {
        // This would need to be handled by the parent function to convert back to individual message
        // For now, keep the group structure
      }

      // Update the group with filtered items
      item.items = filteredItems;
    }

    return true;
  });
}

/**
 * Handles deletion of all attachments for a specific batch
 */
function handleAllAttachmentsDeleted(
  messages: (ChatStory | GroupedLogs)[],
  data: { batchId?: string },
): (ChatStory | GroupedLogs)[] {
  const batchId = data.batchId;

  if (!batchId) return messages;

  return messages.filter((item) => {
    // Remove individual attachments with matching batchId
    if (
      isChatStory(item) &&
      item.batchId === batchId &&
      item.chatType === `App\\Models\\Request-attachment-attachment_added`
    ) {
      return false;
    }

    // Handle grouped attachments
    if (isGroupedAttachments(item)) {
      // Check if this group corresponds to the deleted batch
      const hasMatchingBatch = item.items.some(
        (_subItem) =>
          item.id.includes(batchId) ||
          (item.user &&
            messages.some(
              (msg) =>
                isChatStory(msg) &&
                msg.batchId === batchId &&
                msg.user?.id === item.user?.id,
            )),
      );

      if (hasMatchingBatch) {
        // Remove only attachment items, keep comment items
        const nonAttachmentItems = item.items.filter(
          (subItem) => !subItem.url && !subItem.name,
        );

        if (nonAttachmentItems.length === 0) {
          return false; // Remove entire group
        }

        // Update group with only non-attachment items
        item.items = nonAttachmentItems;
      }
    }

    return true;
  });
}

/**
 * Handles deletion of a specific attachment
 */
function handleAttachmentDeleted(
  messages: (ChatStory | GroupedLogs)[],
  data: { id?: number | string },
): (ChatStory | GroupedLogs)[] {
  const attachmentId = Number(data.id);

  return messages.filter((item) => {
    // Remove individual attachment messages
    if (
      isChatStory(item) &&
      item.attachmentId === attachmentId &&
      item.chatType === `App\\Models\\Request-attachment-attachment_added`
    ) {
      return false;
    }

    // Handle grouped attachments
    if (isGroupedAttachments(item)) {
      const filteredItems = item.items.filter(
        (subItem) =>
          !subItem.url || !subItem.id.includes(attachmentId.toString()),
      );

      if (filteredItems.length === 0) {
        return false; // Remove entire group
      }

      // If only comment items remain (no attachments), might want to ungroup
      const hasAttachments = filteredItems.some(
        (subItem) => subItem.url || subItem.name,
      );

      if (!hasAttachments && filteredItems.length === 1) {
        // Could convert back to individual message, but keeping group for now
      }

      item.items = filteredItems;
    }

    return true;
  });
}
